﻿import * as React from 'react';
import { useState } from 'react';
import { IOrderListItem } from '../../../../Entities';
import * as PlanOrderCombinedStatus from '../../../../view/planOrders/PlanOrderCombinedStatus';
import { Translate } from '../../../../../../shared/LanguageService';
import OrderListRow from '../../../../OrderListRow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
    Accordion, 
    AccordionSummary, 
    AccordionDetails, 
    Table, 
    TableHead, 
    TableRow, 
    TableCell, 
    TableBody, 
    Typography,
    Divider
} from '@mui/material';

export interface IOrderCollection {
    orderCollectionNumber: string;
    orderNumberRange: string;
    firstOrderNumber: string;
    lastOrderNumber: string;
    name: string;
    orders: IOrderListItem[];
    lastOrderPlacedAt: Date;
    city?: string;
}

type OrderCollectionListProps = {
    items: IOrderCollection[];
    user: any;
    navigateTo: (route: string) => void;
    newOrderId: number | null;
};

function createUniqueOrderListRowKey(order: IOrderListItem): string {
    return `${order.orderCollectionNumber}:${order.orderNumber}:${order.orderPlacedAt}:${order.bookingStart}`;
}

function HeadingCell(text: string, style: object) {
    return (
        <Typography sx={style}>
            {text}
        </Typography>
    )
}

const accordionStyles = {
    '&:before': {
        display: 'none',
    },
    boxShadow: "none",
};

function OrderCollectionList({ items, user, navigateTo, newOrderId }: OrderCollectionListProps) {
    const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

    function renderOrderCollectionHeader(orderCollection: IOrderCollection, index: number) {
        if (orderCollection.city) {
            return (
                <>
                    <span>
                        {orderCollection.orderCollectionNumber}:{orderCollection.orderNumberRange} -{' '}
                        {orderCollection.name}, {orderCollection.city}
                        {orderCollection.orders.find((order: IOrderListItem) => {
                            return (
                                order.customerDeliveryAnnotation &&
                                order.status &&
                                order.status.value === PlanOrderCombinedStatus.Delivered
                            );
                        }) &&
                            (user.details.role === 'role/broker-agent' ||
                                user.details.role === 'role/broker-agent-assistant' ||
                                user.details.role === 'role/broker-agent-admin') && (
                                <i
                                    style={{ marginLeft: '10px' }}
                                    className="material-icons"
                                    title={Translate(
                                        'view-order/basic-order-info/not-returned-annotations'
                                    )}
                                >
                                    assignment_return
                                </i>
                            )}
                    </span>
                </>
            );
        } else {
            return (
                <span>
                    {orderCollection.orderCollectionNumber}:{orderCollection.orderNumberRange} -{' '}
                    {orderCollection.name}
                </span>
            );
        }
    }

    const headingStyle = { fontFamily: 'Roboto', fontSize: "14px", fontWeight: "500" };

    const handleAccordionChange = (index: number) => {
        if (expandedIndices.includes(index)) {
            setExpandedIndices(expandedIndices.filter(i => i !== index));
        } else {
            setExpandedIndices([...expandedIndices, index]);
        }
    };

    const listItems = items.map((oc, ocIndex) => {
        const key = oc.orderCollectionNumber;
        const isExpanded = expandedIndices.includes(ocIndex);
        return (
            <div key={oc.orderCollectionNumber}>
                <Accordion
                    sx={accordionStyles}
                    id={`order-collection-group-${ocIndex}`}
                    key={key}
                    expanded={isExpanded}
                    onChange={() => handleAccordionChange(ocIndex)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className="expand-icon"
                        sx={{ flexDirection: "row-reverse" }}
                    >
                        <Typography 
                            sx={{
                                textDecoration: isExpanded ? "underline" : "none",
                                ...headingStyle,
                            }}
                        >
                            {renderOrderCollectionHeader(oc, ocIndex)}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table className="order-table" size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {HeadingCell(Translate('orders/list/header/order-collection-number'), headingStyle)}
                                    </TableCell>
                                    <TableCell>
                                        {HeadingCell(Translate('orders/list/header/order-placed-at'), headingStyle)}
                                    </TableCell>
                                    <TableCell className="hidden-xs">
                                        {HeadingCell(Translate('orders/list/header/booked'), headingStyle)}
                                    </TableCell>
                                    <TableCell className="hidden-xs">
                                        {HeadingCell(Translate('orders/list/header/photographer'), headingStyle)}
                                    </TableCell>
                                    <TableCell className="hidden-xs">
                                        {HeadingCell(Translate('orders/list/header/package'), headingStyle)}
                                    </TableCell>
                                    <TableCell className="hidden-sm hidden-xs" />
                                    <TableCell>
                                        {HeadingCell(Translate('orders/list/header/status'), headingStyle)}
                                    </TableCell>
                                    <TableCell className="hidden-sm hidden-xs" />
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {oc.orders.map((order: IOrderListItem) => (
                                    <OrderListRow
                                        order={order}
                                        key={createUniqueOrderListRowKey(order)}
                                        user={user}
                                        navigateTo={navigateTo}
                                        isNewOrder={newOrderId === order.id.toString()}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
                {!isExpanded && ocIndex !== items.length-1 && <Divider />}
            </div>
        );
    });

    return <div className="accordion-as-table">{listItems}</div>;
}

export default OrderCollectionList;
